<script setup lang="ts">
import { ref, computed } from 'vue'
import { useStore } from 'vuex'

import GuideTooltip from '@/components/Study/Switchers/GuideTooltip.vue'
import SimpleTabs from '@/components/AppBar/SimpleTabs.vue'
import DialogTemplate from '@/components/Dialogs/DialogTemplate.vue'
import GwBtns from '@/components/lib/GwBtns.vue'
import BubbleFactor from '@/components/BubbleFactor/BubbleFactor.vue'
import SolfigrIcmPayoutsTable from '@/components/ConfigureSolution/SolfigrIcmPayoutsTable.vue'
import float_mix from '@/mixins/float_mix'
import { getLinksUrlLinks } from '@/composables/linksMixMethods'

import { useGetTournament } from '@gto/api/vue-query'
import GwBtn from '@/components/lib/GwBtn/GwBtn.vue'
import { SNK } from '@/helpers/helpers'
import { genericPayouts } from './PayoutsDialogConstants'

interface PayoutsDialogConf {
  namespace?: string
  tournamentid?: string
  bubble?: boolean
}

const props = withDefaults(
  defineProps<{
    dialog: PayoutsDialogConf
  }>(),
  {
    dialog: () => ({}),
  },
)

const store = useStore()

const fl_positionTooltip = float_mix.methods.fl_positionTooltip
const links_url_links = getLinksUrlLinks()

const options = ['payouts', 'bubble_factor']
const tab = ref('payouts')
const showPayoutsIn = ref('percent')
const payoutsOptions = [
  { id: 'percent', text: '%' },
  { id: 'dollar', text: '$' },
]

const namespace = computed(() => props.dialog.namespace || '')

const gameformat = computed(() => store.getters[SNK('gameformat', namespace.value)])

const solElite = computed(() => store.getters[SNK('sol_elite', namespace.value)])

const icmTournament = computed(() => ({
  payout_levels: solElite.value.cTree.tournament_data?.tournament_structure?.payout_levels,
}))

const tournamentId = computed(() => {
  const id = props.dialog.tournamentid?.replaceAll('+', '-')
  return id === 'undefined' ? undefined : id
})

const { data: tournamentData } = useGetTournament(tournamentId.value ?? '')

const hasBubble = computed(() => props.dialog.bubble)

const tournamentPayouts = computed(() =>
  tournamentData.value
    ? [
        {
          id: 'EventPayouts',
          name: tournamentData.value.name,
          blocks: [
            {
              rows: tournamentData.value.payout_levels.map((level: any) => [
                `${level.rank}.`,
                `${(parseFloat(level.prize_pool_fraction) * 100).toFixed(2)}%`,
                `$${parseInt(level.prize, 10).toLocaleString()}`,
              ]),
            },
          ],
        },
      ]
    : [],
)

const payouts: any = computed(() => (tournamentId.value ? tournamentPayouts.value : genericPayouts))

const changeShowPayoutsIn = item => {
  showPayoutsIn.value = item.id
}

const close = () => {
  store.dispatch('router_rm_dialog', 'payouts-dialog')
}

const handleTabChange = item => {
  tab.value = item
}
</script>

<template>
  <DialogTemplate
    style="max-width: calc(100vw - 6rem)"
    data-gwmenu="noclose"
  >
    <SimpleTabs
      v-if="hasBubble"
      :value="tab"
      :options
      class="tabs-stretchs"
      style="--height: 3rem; --min-width-tab: 14rem"
      @input="handleTabChange"
    >
      <template #default="v">
        {{ $t(`common.${v.item}`) }}
        <div
          v-if="v.item === 'bubble_factor'"
          class="mdi mdi-help-circle-outline text-medium ml-2 my-auto clr-grey csst"
          @mouseenter="e => fl_positionTooltip(e, { offset: 0, placement: 'bottom' })"
        >
          <div
            class="csst_content clr-white csst_content_hoverable text-wrap"
            style="position: fixed; line-height: 1.2; max-width: 28rem"
            v-html="
              $t('tooltips.bubble_factor_explained', {
                linkText: (!$i18n as any).g1
                  ? `<a target='_blank' class='weight_700 link-underline' href='${
                      links_url_links.icm_pressure_guide.url
                    }'>${$t('guides.icm_pressure')}</a>`
                  : $t('guides.icm_pressure'),
                linkTextBubble: (!$i18n as any).g1
                  ? `<div class='mt-2'><a target='_blank' class='weight_700 link-underline' href='${
                      links_url_links.bubble_factor.url
                    }'>
                      ${$t('guides.bubble_factor_learn_more')}</a></div>`
                  : '',
              })
            "
          ></div>
        </div>
      </template>
    </SimpleTabs>

    <div
      class="d-flex f-wrap"
      style="gap: 1rem"
    >
      <div
        v-if="hasBubble && tab === 'bubble_factor'"
        class="gw_box ma-auto"
      >
        <BubbleFactor
          style="border: solid var(--clr-black-0) 1px; border-radius: 4px; overflow: hidden"
          :gameformat
          :solElite
          class="mt-2"
        />
      </div>

      <SolfigrIcmPayoutsTable
        v-else-if="tab === 'payouts' && solElite.is_using"
        :icmTournament
        class="mt-2 flex-1"
        :hasFooter="false"
        :isEditable="false"
      />

      <div
        v-else-if="tab === 'payouts'"
        class="gw_box"
      >
        <div
          v-if="!hasBubble"
          class="text-large weight_700 clr-primary d-flex"
        >
          {{ $t('common.payouts') }}
        </div>

        <div class="d-flex mt-3 weight_600 mb-4">
          <span class="mr-2 clr-grey">
            {{ $t('payouts_dialog.show_payouts_in') }}
          </span>
          <GwBtns
            :options="payoutsOptions"
            :idKey="'id'"
            :value="showPayoutsIn"
            style="--gwbtn-clr-active-white: var(--gwbtn-clr-hover-white); --gwbtn-weight: 500"
            @click="(_e, item) => changeShowPayoutsIn(item)"
          >
            <template #default="{ item }">
              <div v-html="item.text"></div>
            </template>
          </GwBtns>
        </div>

        <div
          class="d-flex f-wrap mt-2"
          style="gap: 2rem"
        >
          <div
            v-for="item in payouts"
            :key="item.id"
          >
            <div
              class="weight_600 text-normal-large"
              v-html="item.name"
            ></div>
            <div class="d-flex">
              <div
                class="weight_400 text-small text-grey"
                v-html="item.subtitle"
              ></div>
              <GuideTooltip
                v-if="item.guide"
                placement="bottom"
                :guide="item.guide"
                class="my-auto ml-2"
                style="font-size: 0.875rem; --hvr-content-opacity: 1"
              />
            </div>
            <table
              class="d-flex f-wrap mt-3"
              style="gap: 0.5rem"
            >
              <div
                v-for="(block, idx) in item.blocks"
                :key="`${item.id}_${idx}`"
                class="gw_box"
                style="min-width: 7rem; --box-padding: 0.75rem; --clr-box-back: var(--clr-black-2)"
              >
                <div
                  v-if="block.way"
                  class="weight_600"
                >
                  {{ $tc('payouts.num_way', block.way) }}
                </div>
                <table style="width: 8rem">
                  <tr
                    v-for="(row, rIdx) in block.rows"
                    :key="`${item.id}_${idx}_${rIdx}`"
                  >
                    <td class="text-medium text-centers text-nowrap">
                      {{ row[0] }}
                    </td>
                    <td
                      class="text-medium text-nowrap pl-2 text-right"
                      v-html="showPayoutsIn === 'percent' ? row[1] : row[2]"
                    ></td>
                  </tr>
                </table>
              </div>
            </table>
          </div>
        </div>
      </div>
    </div>

    <template #dialog_actions>
      <GwBtn
        variant="btn_large"
        class="ml-auto"
        @click="close"
      >
        {{ $t('btn.close').toUpperCase() }}
      </GwBtn>
    </template>
  </DialogTemplate>
</template>
